import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Lightning, ChartLineUp, ShieldCheck, Robot, Rocket, ArrowRight, Wallet, Globe, Trophy, Target, CurrencyEth, Brain, Cpu, Timer, TrendUp, UsersThree, CurrencyDollar, Star, Lock, Eye, Fingerprint, ShieldStar
} from '@phosphor-icons/react';
import NetworkMarquee from '../components/NetworkMarquee';

const features = [
  { icon: Brain, title: 'AI-Powered Trading Agent', description: 'Neural network scans mempool in real-time, detects patterns, and executes optimal trades 24/7 without human intervention.', color: '#00E5FF' },
  { icon: Lightning, title: 'Sub-Second Execution', description: 'Proprietary low-latency infrastructure places orders in under 200ms, beating frontrunners before blocks are confirmed.', color: '#0047FF' },
  { icon: ShieldCheck, title: 'Anti-MEV Protection', description: 'Flashbots integration and private transaction relay prevent sandwich attacks and protect every trade you make.', color: '#00FF9D' },
  { icon: Globe, title: '7 EVM Networks', description: 'Trade across Ethereum, BSC, Arbitrum, Base, Polygon, Optimism, and Avalanche from a single terminal.', color: '#8247E5' },
  { icon: ChartLineUp, title: 'Live Profit Calculator', description: 'Real-time profit estimates per trade, hour, day, and week that dynamically adjust to market conditions.', color: '#F59E0B' },
  { icon: Cpu, title: 'MEV & Arbitrage Engine', description: 'Advanced multi-DEX arbitrage scanner and mempool analyzer finds spreads of 0.5-2.5% across liquidity pools.', color: '#FF3366' },
];

const advantages = [
  { num: '200ms', label: 'Average execution speed', icon: Timer },
  { num: '94.7%', label: 'Win rate across all modes', icon: Target },
  { num: '7', label: 'EVM networks supported', icon: Globe },
  { num: '50K+', label: 'Active traders worldwide', icon: UsersThree },
  { num: '$847M+', label: 'Total trading volume', icon: CurrencyDollar },
  { num: '4', label: 'Bot modes: Arb, MEV, Mixed, Sniper', icon: Robot },
];

const howItWorks = [
  { step: '01', title: 'Configure Your Bot', desc: 'Choose your network, trading mode, and set your risk parameters with beginner or advanced presets.' },
  { step: '02', title: 'AI Calculates Profit', desc: 'The profit calculator shows estimated returns per hour and recommends the optimal network for maximum gains.' },
  { step: '03', title: 'Connect & Launch', desc: 'Connect your wallet and the AI agent starts scanning the mempool, executing trades, and growing your portfolio.' },
];

const roadmap = [
  { q: 'Q2 2021', title: 'Genesis', items: ['Core team formed', 'Seed round completed', 'Initial algorithm design'], done: true },
  { q: 'Q4 2021', title: 'Alpha', items: ['First trading bot on ETH', 'Private beta with 200 users', 'MEV protection v1'], done: true },
  { q: 'Q2 2022', title: 'Multi-Chain', items: ['BSC & Polygon launch', 'Advanced order types', 'Arbitrage scanner v1'], done: true },
  { q: 'Q4 2022', title: 'Public Launch', items: ['Public release', 'API access tier', '10K+ community'], done: true },
  { q: 'Q2 2023', title: 'AI Integration', items: ['Neural network analysis', 'Sentiment engine', 'Portfolio auto-balance'], done: true },
  { q: 'Q1 2024', title: 'L2 Expansion', items: ['Arbitrum & Optimism', 'Base chain', 'Gas optimization v2'], done: true },
  { q: 'Q1 2025', title: 'Pro Suite', items: ['AI Trading Agent', 'Live profit calculator', 'Advanced presets'], done: true },
  { q: 'Q3 2026', title: 'DeFiBot 2.0', items: ['Cross-chain flash arb', 'Social copy trading', 'Mobile app'], done: false },
];

const securityFeatures = [
  { icon: Lock, title: 'Non-Custodial', desc: 'Your private keys never leave your wallet. We have zero access to your funds or seed phrases.' },
  { icon: ShieldStar, title: 'Flashbots Relay', desc: 'All transactions routed through private mempools. No frontrunning, no sandwich attacks.' },
  { icon: Fingerprint, title: 'Audited by CertiK', desc: 'Smart contracts independently audited. $100K bug bounty program active since 2022.' },
  { icon: Eye, title: 'Open Source Logic', desc: 'Core trading algorithms verifiable on-chain. Full transparency in execution and fee structure.' },
];

const reviews = [
  { name: 'Michael R.', role: 'Full-time DeFi Trader', rating: 5, text: 'Switched from manual trading to DeFiBot 6 months ago. My arbitrage profits went from inconsistent to $2K-5K/week consistently. The AI agent is genuinely impressive.', avatar: 'MR', profit: '+$87,400', period: '6 months' },
  { name: 'Anna K.', role: 'Crypto Fund Manager', rating: 5, text: 'We deployed the Enterprise plan across 3 networks. The MEV protection alone saved us an estimated $40K in sandwich losses. Outstanding infrastructure.', avatar: 'AK', profit: '+$234,000', period: '1 year' },
  { name: 'David L.', role: 'Software Engineer', rating: 5, text: 'As a developer, I appreciate the API access and the clean execution flow. Sub-200ms trades on Arbitrum are real. Verified it myself on Arbiscan.', avatar: 'DL', profit: '+$45,200', period: '4 months' },
  { name: 'Sophie W.', role: 'Independent Trader', rating: 4, text: 'Started with the free Starter plan on Base. Low gas costs + DeFiBot scanner = easy passive income. Upgraded to Pro within 2 weeks.', avatar: 'SW', profit: '+$12,800', period: '3 months' },
  { name: 'James T.', role: 'DeFi Researcher', rating: 5, text: 'The network recommendation engine is brilliant. It auto-switched me from ETH to Base during high gas periods and increased my daily profit by 40%.', avatar: 'JT', profit: '+$67,500', period: '8 months' },
  { name: 'Elena P.', role: 'Quant Trader', rating: 5, text: 'Running Sniper mode for new token launches. The speed advantage is real — consistently among first buyers. ROI has been exceptional.', avatar: 'EP', profit: '+$156,000', period: '10 months' },
];

const ACTIVITY_NAMES = ['0x7a...3f2d', '0x2d...8c41', '0xf1...9e07', '0x3b...a4c9', '0xe8...1d5b', '0x91...c7e3', '0xa4...6f08', '0xc6...2b47', '0x58...d319', '0x1f...e8a2', '0xb3...7c56', '0xd9...4a13'];
const NETWORKS = ['Ethereum', 'BSC', 'Arbitrum', 'Base', 'Polygon', 'Optimism', 'Avalanche'];
const MODES = ['Arbitrage', 'MEV', 'Mixed', 'Sniper'];

const generateActivity = () => {
  const profit = (Math.random() * 2400 + 120).toFixed(0);
  return {
    id: Date.now() + Math.random(),
    user: ACTIVITY_NAMES[Math.floor(Math.random() * ACTIVITY_NAMES.length)],
    network: NETWORKS[Math.floor(Math.random() * NETWORKS.length)],
    mode: MODES[Math.floor(Math.random() * MODES.length)],
    profit: `+$${profit}`,
    time: 'just now',
  };
};

const fadeUp = { hidden: { opacity: 0, y: 40 }, visible: { opacity: 1, y: 0 } };
const stagger = { visible: { transition: { staggerChildren: 0.1 } } };

const LiveActivityFeed = () => {
  const [activities, setActivities] = useState(() => Array.from({ length: 5 }, generateActivity));

  useEffect(() => {
    const interval = setInterval(() => {
      setActivities(prev => {
        const next = [generateActivity(), ...prev.slice(0, 4)];
        return next;
      });
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="space-y-3" data-testid="live-activity-feed">
      <AnimatePresence mode="popLayout">
        {activities.map((a) => (
          <motion.div
            key={a.id}
            layout
            initial={{ opacity: 0, x: -30, scale: 0.95 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            exit={{ opacity: 0, x: 30, scale: 0.95 }}
            transition={{ duration: 0.4 }}
            className="flex items-center justify-between p-3 sm:p-4 rounded-xl bg-[var(--bg-surface-2)] border border-[var(--border-default)] hover:border-[var(--color-primary)]/20 transition-colors"
          >
            <div className="flex items-center gap-2 sm:gap-3 min-w-0">
              <div className="w-7 h-7 sm:w-9 sm:h-9 rounded-lg bg-[var(--accent-success)]/10 flex items-center justify-center flex-shrink-0">
                <TrendUp size={14} className="text-[var(--accent-success)] sm:hidden" weight="bold" />
                <TrendUp size={18} className="text-[var(--accent-success)] hidden sm:block" weight="bold" />
              </div>
              <div className="min-w-0">
                <span className="text-xs sm:text-sm font-mono font-medium text-[var(--text-primary)]">{a.user}</span>
                <span className="text-[10px] sm:text-xs text-[var(--text-secondary)] ml-1 sm:ml-2 hidden sm:inline">earned on {a.network}</span>
                <span className="text-[10px] text-[var(--text-secondary)] ml-1 sm:hidden">{a.network}</span>
              </div>
            </div>
            <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
              <span className="text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-md bg-[var(--bg-surface-1)] text-[var(--text-secondary)] font-medium hidden sm:block">{a.mode}</span>
              <span className="text-xs sm:text-sm font-bold font-mono text-[var(--accent-success)]">{a.profit}</span>
            </div>
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
};

const ORBITAL_NETS = [
  { id: 'eth', name: 'Ethereum', short: 'ETH', color: '#627EEA', speed: 0.0003, radius: 0.85, trades: '847/hr', profit: '$124K', volume: '$324M', winRate: '96.2%' },
  { id: 'bsc', name: 'BSC', short: 'BNB', color: '#F3BA2F', speed: 0.0005, radius: 0.72, trades: '623/hr', profit: '$87K', volume: '$187M', winRate: '94.8%' },
  { id: 'arb', name: 'Arbitrum', short: 'ARB', color: '#28A0F0', speed: 0.0004, radius: 0.6, trades: '534/hr', profit: '$72K', volume: '$156M', winRate: '95.1%' },
  { id: 'base', name: 'Base', short: 'BASE', color: '#0052FF', speed: 0.00065, radius: 0.48, trades: '412/hr', profit: '$54K', volume: '$98M', winRate: '93.7%' },
  { id: 'poly', name: 'Polygon', short: 'POL', color: '#8247E5', speed: 0.00055, radius: 0.36, trades: '389/hr', profit: '$41K', volume: '$76M', winRate: '94.3%' },
  { id: 'opt', name: 'Optimism', short: 'OP', color: '#FF0420', speed: 0.00045, radius: 0.92, trades: '278/hr', profit: '$32K', volume: '$52M', winRate: '93.1%' },
  { id: 'avax', name: 'Avalanche', short: 'AVAX', color: '#E84142', speed: 0.00035, radius: 0.55, trades: '198/hr', profit: '$21K', volume: '$34M', winRate: '92.8%' },
];

const TradingRadar = () => {
  const canvasRef = useRef(null);
  const animRef = useRef(null);
  const stateRef = useRef({ hovered: -1, time: 0, pulses: [], sparkles: [] });
  const [hoveredIdx, setHoveredIdx] = useState(-1);
  const [totalProfit, setTotalProfit] = useState(431000);

  useEffect(() => {
    const profitInterval = setInterval(() => {
      setTotalProfit(p => p + Math.floor(Math.random() * 1500 + 400));
    }, 2500);
    return () => clearInterval(profitInterval);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener('resize', resize);

    // Generate pulse events periodically
    const pulseInterval = setInterval(() => {
      const s = stateRef.current;
      const from = Math.floor(Math.random() * ORBITAL_NETS.length);
      let to = from;
      while (to === from) to = Math.floor(Math.random() * ORBITAL_NETS.length);
      s.pulses.push({ from, to, progress: 0, color: ORBITAL_NETS[from].color });
      // Sparkles at from-node
      for (let k = 0; k < 5; k++) {
        s.sparkles.push({
          x: 0, y: 0, netIdx: from, vx: (Math.random() - 0.5) * 2, vy: (Math.random() - 0.5) * 2,
          life: 1, color: ORBITAL_NETS[from].color,
        });
      }
    }, 1800);

    const getNodePos = (net, time, cx, cy, maxR) => {
      const angle = time * net.speed * 2 * Math.PI + ORBITAL_NETS.indexOf(net) * (Math.PI * 2 / 7);
      const r = net.radius * maxR;
      return { x: cx + Math.cos(angle) * r, y: cy + Math.sin(angle) * r * 0.45 };
    };

    const animate = () => {
      const rect = canvas.getBoundingClientRect();
      const w = rect.width, h = rect.height;
      ctx.clearRect(0, 0, w, h);
      const s = stateRef.current;
      s.time += 1;

      const cx = w / 2, cy = h / 2;
      const maxR = Math.min(w, h) * 0.42;

      // Draw orbit rings
      ORBITAL_NETS.forEach((net) => {
        const r = net.radius * maxR;
        ctx.beginPath();
        ctx.ellipse(cx, cy, r, r * 0.45, 0, 0, Math.PI * 2);
        ctx.strokeStyle = s.hovered === ORBITAL_NETS.indexOf(net) ? `${net.color}30` : 'rgba(100,116,139,0.06)';
        ctx.lineWidth = 1;
        ctx.stroke();
      });

      // Central AI core
      const coreSize = Math.max(4, maxR * 0.1);
      const corePulse = 1 + Math.sin(s.time * 0.03) * 0.08;
      // Glow
      const coreGrad = ctx.createRadialGradient(cx, cy, 0, cx, cy, coreSize * 3 * corePulse);
      coreGrad.addColorStop(0, 'rgba(0,229,255,0.12)');
      coreGrad.addColorStop(0.5, 'rgba(0,71,255,0.04)');
      coreGrad.addColorStop(1, 'transparent');
      ctx.fillStyle = coreGrad;
      ctx.fillRect(cx - coreSize * 3, cy - coreSize * 3, coreSize * 6, coreSize * 6);
      // Core circle
      ctx.beginPath();
      ctx.arc(cx, cy, coreSize * corePulse, 0, Math.PI * 2);
      const innerGrad = ctx.createRadialGradient(cx, cy, 0, cx, cy, coreSize * corePulse);
      innerGrad.addColorStop(0, 'rgba(0,229,255,0.25)');
      innerGrad.addColorStop(1, 'rgba(0,71,255,0.08)');
      ctx.fillStyle = innerGrad;
      ctx.fill();
      ctx.strokeStyle = 'rgba(0,229,255,0.3)';
      ctx.lineWidth = 1;
      ctx.stroke();
      // Core text
      ctx.fillStyle = 'rgba(0,229,255,0.7)';
      ctx.font = `bold ${Math.max(9, coreSize * 0.55)}px "JetBrains Mono", monospace`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('AI', cx, cy);

      // Draw connection lines from each node to core (subtle)
      ORBITAL_NETS.forEach((net, i) => {
        const pos = getNodePos(net, s.time, cx, cy, maxR);
        ctx.beginPath();
        ctx.moveTo(cx, cy);
        ctx.lineTo(pos.x, pos.y);
        ctx.strokeStyle = s.hovered === i ? `${net.color}20` : 'rgba(100,116,139,0.03)';
        ctx.lineWidth = 0.5;
        ctx.stroke();
      });

      // Draw pulses (energy traveling between nodes)
      s.pulses = s.pulses.filter(p => p.progress <= 1);
      s.pulses.forEach(p => {
        p.progress += 0.015;
        const fromPos = getNodePos(ORBITAL_NETS[p.from], s.time, cx, cy, maxR);
        const toPos = getNodePos(ORBITAL_NETS[p.to], s.time, cx, cy, maxR);
        const px = fromPos.x + (toPos.x - fromPos.x) * p.progress;
        const py = fromPos.y + (toPos.y - fromPos.y) * p.progress;
        // Trail
        for (let t = 0; t < 4; t++) {
          const tp = Math.max(0, p.progress - t * 0.03);
          const tx = fromPos.x + (toPos.x - fromPos.x) * tp;
          const ty = fromPos.y + (toPos.y - fromPos.y) * tp;
          ctx.beginPath();
          ctx.arc(tx, ty, 2 - t * 0.4, 0, Math.PI * 2);
          ctx.fillStyle = p.color;
          ctx.globalAlpha = (0.6 - t * 0.15) * (1 - p.progress);
          ctx.fill();
        }
        ctx.globalAlpha = 1;
      });

      // Draw sparkles
      s.sparkles = s.sparkles.filter(sp => sp.life > 0.05);
      s.sparkles.forEach(sp => {
        sp.life -= 0.03;
        if (sp.life <= 0) return;
        const netPos = getNodePos(ORBITAL_NETS[sp.netIdx], s.time, cx, cy, maxR);
        sp.x += sp.vx;
        sp.y += sp.vy;
        const sr = Math.max(0.5, 1.5 * sp.life);
        ctx.beginPath();
        ctx.arc(netPos.x + sp.x, netPos.y + sp.y, sr, 0, Math.PI * 2);
        ctx.fillStyle = sp.color;
        ctx.globalAlpha = Math.max(0, sp.life * 0.6);
        ctx.fill();
        ctx.globalAlpha = 1;
      });

      // Draw network nodes (sorted by y for pseudo-3D depth)
      const nodes = ORBITAL_NETS.map((net, i) => ({ ...net, i, ...getNodePos(net, s.time, cx, cy, maxR) }));
      nodes.sort((a, b) => a.y - b.y);

      nodes.forEach(({ i, x, y, color, short }) => {
        const isHov = s.hovered === i;
        const nodeR = isHov ? 22 : 14;
        const depth = Math.max(0.3, 0.6 + 0.4 * ((y - (cy - maxR * 0.45)) / (maxR * 0.9)));
        const drawR = Math.max(1, nodeR * depth);
        const innerR = Math.max(0.5, (isHov ? 5 : 3.5) * depth);

        // Node glow
        if (isHov) {
          const g = ctx.createRadialGradient(x, y, 0, x, y, drawR * 3);
          g.addColorStop(0, `${color}25`);
          g.addColorStop(1, 'transparent');
          ctx.fillStyle = g;
          ctx.fillRect(x - drawR * 3, y - drawR * 3, drawR * 6, drawR * 6);
        }

        // Node circle
        ctx.beginPath();
        ctx.arc(x, y, drawR, 0, Math.PI * 2);
        ctx.fillStyle = isHov ? `${color}40` : `${color}18`;
        ctx.fill();
        ctx.strokeStyle = isHov ? color : `${color}50`;
        ctx.lineWidth = isHov ? 2 : 1;
        ctx.stroke();

        // Inner dot
        ctx.beginPath();
        ctx.arc(x, y, innerR, 0, Math.PI * 2);
        ctx.fillStyle = color;
        ctx.shadowColor = color;
        ctx.shadowBlur = isHov ? 12 : 4;
        ctx.fill();
        ctx.shadowBlur = 0;

        // Label
        ctx.fillStyle = isHov ? color : `${color}BB`;
        ctx.font = `bold ${isHov ? 11 : 9}px "JetBrains Mono", monospace`;
        ctx.textAlign = 'center';
        ctx.fillText(short, x, y + drawR + 14);
      });

      animRef.current = requestAnimationFrame(animate);
    };

    // Mouse tracking
    const handleMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      const mx = e.clientX - rect.left, my = e.clientY - rect.top;
      const cx = rect.width / 2, cy = rect.height / 2;
      const maxR = Math.min(rect.width, rect.height) * 0.42;
      let found = -1;
      ORBITAL_NETS.forEach((net, i) => {
        const pos = getNodePos(net, stateRef.current.time, cx, cy, maxR);
        const dx = pos.x - mx, dy = pos.y - my;
        if (Math.sqrt(dx * dx + dy * dy) < 28) found = i;
      });
      stateRef.current.hovered = found;
      setHoveredIdx(found);
    };
    const handleLeave = () => { stateRef.current.hovered = -1; setHoveredIdx(-1); };

    canvas.addEventListener('mousemove', handleMove);
    canvas.addEventListener('mouseleave', handleLeave);
    // Touch support
    canvas.addEventListener('touchstart', (e) => {
      const touch = e.touches[0];
      handleMove({ clientX: touch.clientX, clientY: touch.clientY });
    });

    animRef.current = requestAnimationFrame(animate);
    return () => {
      cancelAnimationFrame(animRef.current);
      clearInterval(pulseInterval);
      window.removeEventListener('resize', resize);
      canvas.removeEventListener('mousemove', handleMove);
      canvas.removeEventListener('mouseleave', handleLeave);
    };
  }, []);

  const activeNet = hoveredIdx >= 0 ? ORBITAL_NETS[hoveredIdx] : null;

  return (
    <div className="relative w-full" data-testid="trading-radar">
      <div className="relative">
        <canvas
          ref={canvasRef}
          className="w-full cursor-crosshair"
          style={{ height: '100%', minHeight: '400px' }}
        />

        {/* Subtle profit counter */}
        <div className="absolute top-2 right-3 pointer-events-none">
          <motion.div key={totalProfit} initial={{ y: -6, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="text-right">
            <span className="text-[10px] text-[var(--text-secondary)]/60 block">24h Profit</span>
            <span className="text-xs font-bold font-mono text-[var(--accent-success)]">${totalProfit.toLocaleString()}</span>
          </motion.div>
        </div>

        {/* Hovered network detail popup */}
        <AnimatePresence>
          {activeNet && (
            <motion.div
              key={activeNet.id}
              initial={{ opacity: 0, y: 10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.95 }}
              transition={{ duration: 0.2 }}
              className="absolute bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:w-80 p-4 rounded-2xl border backdrop-blur-xl pointer-events-none"
              style={{ borderColor: `${activeNet.color}40`, background: 'rgba(2,4,10,0.85)' }}
            >
              <div className="flex items-center gap-2.5 mb-3">
                <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: `${activeNet.color}20` }}>
                  <div className="w-3 h-3 rounded-full" style={{ background: activeNet.color, boxShadow: `0 0 8px ${activeNet.color}` }} />
                </div>
                <span className="text-sm font-bold text-[var(--text-primary)]">{activeNet.name}</span>
                <span className="ml-auto text-[10px] px-2 py-0.5 rounded-md font-mono font-bold text-[var(--accent-success)] bg-[var(--accent-success)]/10">Scanning</span>
              </div>
              <div className="grid grid-cols-4 gap-3">
                {[
                  { label: 'Trades', value: activeNet.trades },
                  { label: 'Profit 24h', value: activeNet.profit },
                  { label: 'Volume', value: activeNet.volume },
                  { label: 'Win Rate', value: activeNet.winRate },
                ].map((s) => (
                  <div key={s.label}>
                    <p className="text-[9px] uppercase tracking-wider text-[var(--text-secondary)] mb-0.5">{s.label}</p>
                    <p className="text-xs font-bold font-mono" style={{ color: s.label === 'Profit 24h' ? 'var(--accent-success)' : 'var(--text-primary)' }}>{s.value}</p>
                  </div>
                ))}
              </div>
              {/* Mini bar chart */}
              <div className="mt-3 flex gap-[1.5px] items-end h-6">
                {Array.from({ length: 30 }, (_, i) => (
                  <motion.div key={i} className="flex-1 rounded-sm" style={{ background: activeNet.color, height: `${20 + Math.random() * 80}%`, opacity: 0.3 + Math.random() * 0.5 }} initial={{ scaleY: 0 }} animate={{ scaleY: 1 }} transition={{ duration: 0.3, delay: i * 0.01 }} />
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

const TrustpilotBadge = () => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    className="inline-flex items-center gap-2 sm:gap-3 px-4 sm:px-6 py-2.5 sm:py-3 rounded-xl sm:rounded-2xl bg-[var(--bg-surface-1)] border border-[var(--border-default)]"
    data-testid="trustpilot-badge"
  >
    <div className="flex gap-0.5">
      {[1,2,3,4,5].map(i => (
        <Star key={i} size={14} weight="fill" className={i <= 4 ? 'text-[#00B67A]' : 'text-[#00B67A]/50'} />
      ))}
    </div>
    <div className="h-4 sm:h-5 w-px bg-[var(--border-default)]" />
    <div>
      <span className="text-xs sm:text-sm font-bold text-[var(--text-primary)]">4.8/5</span>
      <span className="text-[10px] sm:text-xs text-[var(--text-secondary)] ml-1">Trustpilot</span>
    </div>
    <div className="h-4 sm:h-5 w-px bg-[var(--border-default)] hidden sm:block" />
    <span className="text-[10px] sm:text-xs text-[var(--text-secondary)] hidden sm:block">2,847 reviews</span>
  </motion.div>
);

const Home = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* ====== HERO ====== */}
      <section className="relative min-h-[100svh] flex items-center overflow-hidden">
        <div className="absolute inset-0 bg-[var(--bg-primary)]">
          <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: 'linear-gradient(var(--border-default) 1px, transparent 1px), linear-gradient(90deg, var(--border-default) 1px, transparent 1px)', backgroundSize: '80px 80px' }} />
          <motion.div className="absolute top-[10%] left-[15%] w-[500px] sm:w-[700px] h-[500px] sm:h-[700px] rounded-full" style={{ background: 'radial-gradient(circle, rgba(0,229,255,0.12) 0%, transparent 60%)' }} animate={{ scale: [1, 1.3, 1], x: [0, 40, 0] }} transition={{ duration: 12, repeat: Infinity }} />
          <motion.div className="absolute bottom-[15%] right-[10%] w-[400px] sm:w-[600px] h-[400px] sm:h-[600px] rounded-full" style={{ background: 'radial-gradient(circle, rgba(0,71,255,0.12) 0%, transparent 60%)' }} animate={{ scale: [1.2, 1, 1.2], y: [0, -50, 0] }} transition={{ duration: 10, repeat: Infinity }} />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 items-center">
            {/* Left — Text */}
            <motion.div initial="hidden" animate="visible" variants={stagger} className="text-left">
              <motion.div variants={fadeUp} className="inline-flex items-center gap-2 sm:gap-3 px-3 sm:px-5 py-2 sm:py-2.5 rounded-full border border-[var(--color-primary)]/30 bg-[var(--color-primary)]/5 mb-6 sm:mb-8">
                <span className="relative flex h-2.5 w-2.5 sm:h-3 sm:w-3"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[var(--accent-success)] opacity-75"></span><span className="relative inline-flex rounded-full h-2.5 w-2.5 sm:h-3 sm:w-3 bg-[var(--accent-success)]"></span></span>
                <span className="text-xs sm:text-sm font-medium text-[var(--color-primary)]">AI Agent Active on 7 Networks</span>
              </motion.div>

              <motion.h1 variants={fadeUp} className="text-3xl sm:text-5xl lg:text-7xl font-bold tracking-tight mb-4 sm:mb-6">
                <span className="text-[var(--text-primary)]">Maximize </span>
                <span className="bg-gradient-to-r from-[#00E5FF] via-[#0047FF] to-[#8B5CF6] bg-clip-text text-transparent" style={{ backgroundSize: '200% 200%', animation: 'gradient-shift 5s ease infinite' }}>Extractable</span>
                <br />
                <span className="text-[var(--text-primary)]">Value</span>
              </motion.h1>

              <motion.p variants={fadeUp} className="text-sm sm:text-base lg:text-lg text-[var(--text-secondary)] max-w-xl mb-6 sm:mb-8 leading-relaxed">
                AI-powered trading terminal that scans mempools, finds arbitrage across 7 networks, and executes profitable trades in under 200ms.
              </motion.p>

              <motion.div variants={fadeUp} className="mb-6 sm:mb-8">
                <TrustpilotBadge />
              </motion.div>

              <motion.div variants={fadeUp} className="flex flex-col sm:flex-row items-stretch sm:items-start gap-3 sm:gap-4">
                <motion.button whileHover={{ scale: 1.04, boxShadow: '0 0 50px rgba(0,229,255,0.4)' }} whileTap={{ scale: 0.97 }} onClick={() => navigate('/dashboard')}
                  className="flex items-center justify-center gap-3 px-6 sm:px-8 py-3.5 sm:py-4 rounded-xl sm:rounded-2xl text-sm sm:text-base font-bold transition-all" style={{ background: 'linear-gradient(135deg, #00E5FF, #0047FF)', color: '#02040A', boxShadow: '0 0 30px rgba(0,229,255,0.3)' }} data-testid="hero-launch-app">
                  <CurrencyEth size={20} weight="bold" /> Launch Terminal
                </motion.button>
                <Link to="/pricing" className="sm:inline-flex">
                  <motion.button whileHover={{ scale: 1.04, borderColor: '#00E5FF' }} whileTap={{ scale: 0.97 }}
                    className="w-full sm:w-auto flex items-center justify-center gap-2 px-6 sm:px-8 py-3.5 sm:py-4 rounded-xl sm:rounded-2xl text-sm sm:text-base font-bold border border-[var(--border-default)] text-[var(--text-primary)] bg-[var(--bg-surface-1)]/50 backdrop-blur-sm" data-testid="hero-view-pricing">
                    View Pricing <ArrowRight size={16} />
                  </motion.button>
                </Link>
              </motion.div>
            </motion.div>

            {/* Right — Orbital Visualization */}
            <motion.div initial={{ opacity: 0, x: 60 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 1, delay: 0.3 }} className="hidden lg:block">
              <TradingRadar />
            </motion.div>
          </div>

          {/* Mobile orbital — below text */}
          <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.5 }} className="mt-8 lg:hidden">
            <TradingRadar />
          </motion.div>
        </div>
      </section>

      <NetworkMarquee />

      {/* ====== LIVE ACTIVITY ====== */}
      <section className="py-12 sm:py-20 relative overflow-hidden" data-testid="live-activity-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
              <motion.div variants={fadeUp} className="flex items-center gap-2 mb-3 sm:mb-4">
                <span className="relative flex h-2.5 w-2.5"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[var(--accent-success)] opacity-75"></span><span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[var(--accent-success)]"></span></span>
                <span className="text-xs sm:text-sm uppercase tracking-[0.2em] text-[var(--accent-success)] font-semibold">Live Activity</span>
              </motion.div>
              <motion.h2 variants={fadeUp} className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[var(--text-primary)] mb-3 sm:mb-4">Real Traders,<br />Real Profits</motion.h2>
              <motion.p variants={fadeUp} className="text-sm sm:text-base text-[var(--text-secondary)] mb-5 sm:mb-6 leading-relaxed">Watch DeFiBot users earn in real-time across 7 supported EVM networks.</motion.p>
              <motion.div variants={fadeUp} className="flex items-center gap-4 sm:gap-6">
                <div><span className="text-xl sm:text-2xl font-bold text-[var(--text-primary)]" style={{ fontFamily: 'JetBrains Mono' }}>3,847</span><p className="text-[10px] sm:text-xs text-[var(--text-secondary)]">Bots active now</p></div>
                <div className="h-8 sm:h-10 w-px bg-[var(--border-default)]" />
                <div><span className="text-xl sm:text-2xl font-bold text-[var(--accent-success)]" style={{ fontFamily: 'JetBrains Mono' }}>$1.2M+</span><p className="text-[10px] sm:text-xs text-[var(--text-secondary)]">Earned today</p></div>
              </motion.div>
            </motion.div>
            <div>
              <LiveActivityFeed />
            </div>
          </div>
        </div>
      </section>

      {/* ====== ADVANTAGES ====== */}
      <section className="py-14 sm:py-24 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-10 sm:mb-16">
            <motion.span variants={fadeUp} className="text-xs sm:text-sm uppercase tracking-[0.2em] text-[var(--color-primary)] font-semibold">Why DeFiBot</motion.span>
            <motion.h2 variants={fadeUp} className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[var(--text-primary)] mt-3 sm:mt-4">Built for Performance</motion.h2>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid grid-cols-3 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
            {advantages.map((a, i) => (
              <motion.div key={i} variants={fadeUp} whileHover={{ y: -8, borderColor: '#00E5FF' }} className="p-3 sm:p-5 rounded-xl sm:rounded-2xl bg-[var(--bg-surface-1)] border border-[var(--border-default)] text-center transition-all group" data-testid={`advantage-${i}`}>
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl bg-[var(--color-primary)]/10 flex items-center justify-center mx-auto mb-2 sm:mb-3 group-hover:bg-[var(--color-primary)]/20 transition-all">
                  <a.icon size={16} className="text-[var(--color-primary)] sm:hidden" weight="bold" />
                  <a.icon size={20} className="text-[var(--color-primary)] hidden sm:block" weight="bold" />
                </div>
                <div className="text-lg sm:text-2xl font-bold text-[var(--text-primary)] mb-0.5 sm:mb-1" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{a.num}</div>
                <div className="text-[9px] sm:text-xs text-[var(--text-secondary)] leading-tight">{a.label}</div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ====== FEATURES ====== */}
      <section className="py-14 sm:py-24 bg-[var(--bg-surface-1)]/50" id="features">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-10 sm:mb-16">
            <motion.span variants={fadeUp} className="text-xs sm:text-sm uppercase tracking-[0.2em] text-[var(--color-primary)] font-semibold">Features</motion.span>
            <motion.h2 variants={fadeUp} className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[var(--text-primary)] mt-3 sm:mt-4">Institutional-Grade Tools</motion.h2>
            <motion.p variants={fadeUp} className="text-sm sm:text-base text-[var(--text-secondary)] mt-3 sm:mt-4 max-w-2xl mx-auto">Every feature designed to extract maximum value from decentralized markets.</motion.p>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {features.map((f, i) => (
              <motion.div key={i} variants={fadeUp} whileHover={{ y: -6, borderColor: f.color, boxShadow: `0 20px 60px ${f.color}15` }}
                className="p-5 sm:p-7 rounded-xl sm:rounded-2xl bg-[var(--bg-surface-2)] border border-[var(--border-default)] transition-all group" data-testid={`feature-${i}`}>
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl flex items-center justify-center mb-3 sm:mb-5 transition-all" style={{ backgroundColor: `${f.color}15` }}>
                  <f.icon size={20} style={{ color: f.color }} weight="bold" />
                </div>
                <h3 className="text-base sm:text-lg font-bold text-[var(--text-primary)] mb-1.5 sm:mb-2">{f.title}</h3>
                <p className="text-[var(--text-secondary)] text-xs sm:text-sm leading-relaxed">{f.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ====== SECURITY ====== */}
      <section className="py-14 sm:py-24" id="security" data-testid="security-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-10 sm:mb-16">
            <motion.span variants={fadeUp} className="text-xs sm:text-sm uppercase tracking-[0.2em] text-[var(--accent-success)] font-semibold">Security</motion.span>
            <motion.h2 variants={fadeUp} className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[var(--text-primary)] mt-3 sm:mt-4">Your Funds, Your Keys</motion.h2>
            <motion.p variants={fadeUp} className="text-sm sm:text-base text-[var(--text-secondary)] mt-3 sm:mt-4 max-w-2xl mx-auto">100% non-custodial. We never touch your private keys. Every transaction is signed locally and protected by Flashbots.</motion.p>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
            {securityFeatures.map((s, i) => (
              <motion.div key={i} variants={fadeUp} whileHover={{ y: -6, borderColor: '#00FF9D' }}
                className="p-4 sm:p-6 rounded-xl sm:rounded-2xl bg-[var(--bg-surface-1)] border border-[var(--border-default)] transition-all" data-testid={`security-feature-${i}`}>
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl bg-[var(--accent-success)]/10 flex items-center justify-center mb-3 sm:mb-4">
                  <s.icon size={20} className="text-[var(--accent-success)]" weight="bold" />
                </div>
                <h3 className="text-sm sm:text-lg font-bold text-[var(--text-primary)] mb-1 sm:mb-2">{s.title}</h3>
                <p className="text-xs sm:text-sm text-[var(--text-secondary)] leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </motion.div>
          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="mt-6 sm:mt-10 p-4 sm:p-6 rounded-xl sm:rounded-2xl border border-[var(--accent-success)]/20 bg-[var(--accent-success)]/5 text-center">
            <p className="text-xs sm:text-sm text-[var(--text-secondary)]">
              Audited by <span className="text-[var(--text-primary)] font-semibold">CertiK</span> & <span className="text-[var(--text-primary)] font-semibold">OpenZeppelin</span> &middot; Bug bounty <span className="text-[var(--accent-success)] font-bold">$100K</span> &middot; Zero incidents
            </p>
          </motion.div>
        </div>
      </section>

      {/* ====== HOW IT WORKS ====== */}
      <section className="py-14 sm:py-24 bg-[var(--bg-surface-1)]/50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-10 sm:mb-16">
            <motion.span variants={fadeUp} className="text-xs sm:text-sm uppercase tracking-[0.2em] text-[var(--color-primary)] font-semibold">How It Works</motion.span>
            <motion.h2 variants={fadeUp} className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[var(--text-primary)] mt-3 sm:mt-4">Start Earning in 3 Steps</motion.h2>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="space-y-4 sm:space-y-8">
            {howItWorks.map((s, i) => (
              <motion.div key={i} variants={fadeUp} whileHover={{ x: 8 }}
                className="flex items-start gap-4 sm:gap-6 p-5 sm:p-8 rounded-xl sm:rounded-2xl bg-[var(--bg-surface-2)] border border-[var(--border-default)] hover:border-[var(--color-primary)]/40 transition-all" data-testid={`step-${i}`}>
                <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-xl sm:rounded-2xl flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg, rgba(0,229,255,0.15), rgba(0,71,255,0.15))' }}>
                  <span className="text-lg sm:text-2xl font-bold text-[var(--color-primary)]" style={{ fontFamily: 'JetBrains Mono' }}>{s.step}</span>
                </div>
                <div><h3 className="text-base sm:text-xl font-bold text-[var(--text-primary)] mb-1 sm:mb-2">{s.title}</h3><p className="text-xs sm:text-base text-[var(--text-secondary)] leading-relaxed">{s.desc}</p></div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ====== REVIEWS ====== */}
      <section className="py-14 sm:py-24" id="reviews" data-testid="reviews-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-10 sm:mb-16">
            <motion.span variants={fadeUp} className="text-xs sm:text-sm uppercase tracking-[0.2em] text-[var(--color-primary)] font-semibold">Reviews</motion.span>
            <motion.h2 variants={fadeUp} className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[var(--text-primary)] mt-3 sm:mt-4">Trusted by 50,000+ Traders</motion.h2>
            <motion.div variants={fadeUp} className="mt-4 sm:mt-6">
              <TrustpilotBadge />
            </motion.div>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {reviews.map((r, i) => (
              <motion.div key={i} variants={fadeUp} whileHover={{ y: -4 }}
                className="p-4 sm:p-6 rounded-xl sm:rounded-2xl bg-[var(--bg-surface-1)] border border-[var(--border-default)] hover:border-[var(--color-primary)]/20 transition-all" data-testid={`review-${i}`}>
                <div className="flex items-center gap-3 mb-3 sm:mb-4">
                  <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-gradient-to-br from-[var(--color-primary)] to-[var(--color-secondary)] flex items-center justify-center text-[10px] sm:text-xs font-bold text-white">{r.avatar}</div>
                  <div>
                    <p className="text-xs sm:text-sm font-bold text-[var(--text-primary)]">{r.name}</p>
                    <p className="text-[10px] sm:text-xs text-[var(--text-secondary)]">{r.role}</p>
                  </div>
                </div>
                <div className="flex gap-0.5 mb-2 sm:mb-3">
                  {[1,2,3,4,5].map(s => <Star key={s} size={12} weight="fill" className={s <= r.rating ? 'text-[#F59E0B]' : 'text-[var(--text-secondary)]/30'} />)}
                </div>
                <p className="text-xs sm:text-sm text-[var(--text-secondary)] leading-relaxed mb-3 sm:mb-4">{r.text}</p>
                <div className="flex items-center gap-3 pt-2 sm:pt-3 border-t border-[var(--border-default)]">
                  <span className="text-xs sm:text-sm font-bold font-mono text-[var(--accent-success)]">{r.profit}</span>
                  <span className="text-[10px] sm:text-xs text-[var(--text-secondary)]">in {r.period}</span>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ====== ROADMAP ====== */}
      <section className="py-14 sm:py-24 bg-[var(--bg-surface-1)]/50" id="roadmap">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-10 sm:mb-16">
            <motion.span variants={fadeUp} className="text-xs sm:text-sm uppercase tracking-[0.2em] text-[var(--color-primary)] font-semibold">Roadmap</motion.span>
            <motion.h2 variants={fadeUp} className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[var(--text-primary)] mt-3 sm:mt-4">Our Journey</motion.h2>
          </motion.div>
          <div className="relative">
            <div className="hidden md:block absolute top-6 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[var(--color-primary)]/40 to-transparent" />
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-6">
              {roadmap.map((r, i) => (
                <motion.div key={i} variants={fadeUp} whileHover={{ y: -6 }}
                  className={`relative pt-8 sm:pt-10 p-3 sm:p-5 rounded-xl sm:rounded-2xl border transition-all ${r.done ? 'bg-[var(--bg-surface-2)] border-[var(--color-primary)]/30' : 'bg-[var(--bg-surface-2)]/50 border-[var(--border-default)] border-dashed'}`} data-testid={`roadmap-${i}`}>
                  <div className="absolute top-2 sm:top-3 left-3 sm:left-5 flex items-center gap-1.5 sm:gap-2">
                    <div className={`w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full ${r.done ? 'bg-[var(--color-primary)] shadow-[0_0_8px_rgba(0,229,255,0.6)]' : 'bg-[var(--text-secondary)]/40 border border-[var(--text-secondary)]/40'}`} />
                    <span className={`text-[10px] sm:text-xs font-mono font-bold ${r.done ? 'text-[var(--color-primary)]' : 'text-[var(--text-secondary)]'}`}>{r.q}</span>
                    {r.done && <Trophy size={10} className="text-[var(--accent-success)] hidden sm:block" weight="fill" />}
                  </div>
                  <h3 className="text-xs sm:text-base font-bold text-[var(--text-primary)] mb-1 sm:mb-2">{r.title}</h3>
                  <ul className="space-y-0.5 sm:space-y-1">{r.items.map((p, j) => <li key={j} className="text-[10px] sm:text-xs text-[var(--text-secondary)]">{p}</li>)}</ul>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* ====== CTA ====== */}
      <section className="py-14 sm:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }}
            className="p-8 sm:p-14 rounded-2xl sm:rounded-3xl border border-[var(--color-primary)]/20 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, rgba(0,229,255,0.08), rgba(0,71,255,0.04), rgba(139,92,246,0.04))' }}>
            <motion.div className="absolute -top-20 -right-20 w-40 sm:w-60 h-40 sm:h-60 rounded-full bg-[var(--color-primary)]/10" animate={{ scale: [1, 1.2, 1] }} transition={{ duration: 5, repeat: Infinity }} />
            <h2 className="text-2xl sm:text-3xl lg:text-5xl font-bold text-[var(--text-primary)] mb-4 sm:mb-6">Ready to Extract Value?</h2>
            <p className="text-sm sm:text-lg text-[var(--text-secondary)] mb-6 sm:mb-10 max-w-xl mx-auto relative z-10">Join 50,000+ traders using DeFiBot across 7 EVM networks. Start with a free trial.</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 relative z-10">
              <motion.button whileHover={{ scale: 1.04, boxShadow: '0 0 50px rgba(0,229,255,0.4)' }} whileTap={{ scale: 0.97 }} onClick={() => navigate('/dashboard')}
                className="w-full sm:w-auto flex items-center justify-center gap-3 px-8 py-4 sm:px-10 sm:py-5 rounded-xl sm:rounded-2xl text-sm sm:text-lg font-bold" style={{ background: 'linear-gradient(135deg, #00E5FF, #0047FF)', color: '#02040A' }} data-testid="cta-launch-app">
                <Wallet size={20} weight="bold" /> Launch Terminal Free
              </motion.button>
              <Link to="/about" className="w-full sm:w-auto">
                <motion.button whileHover={{ scale: 1.04 }} className="w-full flex items-center justify-center gap-2 px-8 py-4 sm:px-10 sm:py-5 rounded-xl sm:rounded-2xl text-sm sm:text-lg font-bold text-[var(--text-primary)] border border-[var(--border-default)]">
                  Learn More <ArrowRight size={18} />
                </motion.button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;
